import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import { supabase } from "../supabase";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

export default function Analytics() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    fetchDrivers();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchDrivers = async () => {
    const { data } = await supabase
      .from("drivers")
      .select("*")
      .order("submitted_at", { ascending: true });
    setDrivers(data || []);
    setLoading(false);
  };

  const total = drivers.length;
  const drugPassed = drivers.filter((d) => d.drug_test === "pass").length;
  const drugFailed = drivers.filter((d) => d.drug_test === "fail").length;
  const cdlPassed = drivers.filter((d) => d.cdl_history === "pass").length;
  const cdlFailed = drivers.filter((d) => d.cdl_history === "fail").length;
  const contractSent = drivers.filter((d) => d.contract_sent === "sent").length;
  const hasCorp = drivers.filter((d) => d.has_corp === "Yes").length;
  const noCorp = drivers.filter((d) => d.has_corp === "No").length;
  const passRate = total > 0 ? Math.round((drugPassed / total) * 100) : 0;

  const appsByDay = drivers.reduce((acc, driver) => {
    const date = new Date(driver.submitted_at).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    acc[date] = (acc[date] || 0) + 1;
    return acc;
  }, {});
  const barData = Object.entries(appsByDay).map(([date, count]) => ({
    date,
    count,
  }));

  const funnelData = [
    { name: "Applied", value: total, color: "#3a9e5f" },
    { name: "Drug test passed", value: drugPassed, color: "#5bc08a" },
    { name: "CDL passed", value: cdlPassed, color: "#85c1e9" },
    { name: "Contract sent", value: contractSent, color: "#185fa5" },
  ];

  const corpData = [
    { name: "Has corporation", value: hasCorp, color: "#3a9e5f" },
    { name: "No corporation", value: noCorp, color: "#e8e8e8" },
  ];

  const statCards = [
    { label: "Total applicants", value: total, color: "#111" },
    { label: "Drug test pass rate", value: `${passRate}%`, color: "#3a9e5f" },
    { label: "Drug test failed", value: drugFailed, color: "#a32d2d" },
    { label: "CDL history failed", value: cdlFailed, color: "#a32d2d" },
    { label: "Contracts sent", value: contractSent, color: "#185fa5" },
    { label: "Own corporation", value: hasCorp, color: "#854f0b" },
  ];

  // Shared card title style
  const cardTitle = {
    fontSize: "13px",
    fontWeight: "500",
    letterSpacing: "-0.01em",
    color: "#111",
    marginBottom: "16px",
  };
  const card = {
    background: "#fff",
    border: "0.5px solid #e8e8e8",
    borderRadius: "12px",
    padding: "20px",
  };

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          minHeight: "100vh",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        {!isMobile && <Sidebar />}
        {isMobile && <MobileHeader title="Analytics" />}
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          className="t-body"
        >
          Loading...
        </div>
      </div>
    );

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f8f8f8" }}>
      {!isMobile && <Sidebar />}

      <div style={{ flex: 1, minWidth: 0 }}>
        {isMobile && <MobileHeader title="Analytics" />}

        {/* Top bar */}
        <div
          style={{
            background: "#fff",
            borderBottom: "0.5px solid #e8e8e8",
            padding: "12px 24px",
          }}
        >
          <div className="t-page-title">Analytics</div>
        </div>

        <div style={{ padding: isMobile ? "12px" : "20px 24px" }}>
          {/* Stat cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "repeat(2, 1fr)"
                : "repeat(6, 1fr)",
              gap: "10px",
              marginBottom: "20px",
            }}
          >
            {statCards.map((stat) => (
              <div
                key={stat.label}
                style={{
                  background: "#fff",
                  border: "0.5px solid #e8e8e8",
                  borderRadius: "10px",
                  padding: "14px",
                }}
              >
                {/* Label: t-label */}
                <div className="t-label" style={{ marginBottom: "6px" }}>
                  {stat.label}
                </div>
                {/* Value: t-stat */}
                <div
                  className="t-stat"
                  style={{
                    fontSize: isMobile ? "20px" : undefined,
                    color: stat.color,
                  }}
                >
                  {stat.value}
                </div>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
              gap: "16px",
              marginBottom: "16px",
            }}
          >
            {/* Applications over time */}
            <div style={card}>
              <div style={cardTitle}>Applications over time</div>
              {barData.length === 0 ? (
                <div
                  style={{
                    height: "200px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  className="t-label"
                >
                  No data yet
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={barData}>
                    <XAxis
                      dataKey="date"
                      tick={{
                        fontSize: 11,
                        fill: "#999",
                        fontFamily: "inherit",
                      }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{
                        fontSize: 11,
                        fill: "#999",
                        fontFamily: "inherit",
                      }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        fontSize: "12px",
                        border: "0.5px solid #e8e8e8",
                        borderRadius: "8px",
                        fontFamily: "inherit",
                      }}
                      cursor={{ fill: "#f5f5f5" }}
                    />
                    <Bar
                      dataKey="count"
                      fill="#3a9e5f"
                      radius={[4, 4, 0, 0]}
                      name="Applications"
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Corp breakdown */}
            <div style={card}>
              <div style={cardTitle}>Corporation breakdown</div>
              {total === 0 ? (
                <div
                  style={{
                    height: "200px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  className="t-label"
                >
                  No data yet
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={corpData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {corpData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ fontSize: "12px", fontFamily: "inherit" }}
                    />
                    <Tooltip
                      contentStyle={{
                        fontSize: "12px",
                        border: "0.5px solid #e8e8e8",
                        borderRadius: "8px",
                        fontFamily: "inherit",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Pipeline funnel */}
          <div style={card}>
            <div style={cardTitle}>Hiring pipeline</div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "flex-end",
                height: "120px",
              }}
            >
              {funnelData.map((item, index) => {
                const height =
                  total > 0 ? Math.max((item.value / total) * 100, 4) : 4;
                return (
                  <div
                    key={index}
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "6px",
                      height: "100%",
                      justifyContent: "flex-end",
                    }}
                  >
                    {/* Funnel number */}
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "600",
                        letterSpacing: "-0.02em",
                        color: "#111",
                      }}
                    >
                      {item.value}
                    </div>
                    <div
                      style={{
                        width: "100%",
                        height: `${height}%`,
                        background: item.color,
                        borderRadius: "4px 4px 0 0",
                        minHeight: "4px",
                      }}
                    />
                    {/* Funnel label */}
                    <div
                      className="t-table-header"
                      style={{
                        textTransform: "none",
                        letterSpacing: "0",
                        color: "#9ca3af",
                        textAlign: "center",
                      }}
                    >
                      {item.name}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
